import { forwardRef, type ReactNode } from 'react';
import { ActionIcon, Group, Pill as MantinePill } from '@mantine/core';
import { X } from 'lucide-react';
import { alpha, inks } from '../../theme/tokens';

export type PillVariant = 'success' | 'info' | 'warn' | 'danger' | 'neutral';
export type PillSize = 'xs' | 'sm' | 'md';

export interface ForgePillProps {
  variant?: PillVariant;
  size?: PillSize;
  dismissible?: boolean;
  onDismiss?: () => void;
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Accessible label for the dismiss button. Defaults to "Remove". */
  dismissLabel?: string;
}

const VARIANT_TO_COLOR: Record<PillVariant, { fg: string; bg: string; border: string }> = {
  // fg = accent INK so the label clears AA on the 12% wash in BOTH themes.
  success: { fg: inks.neon, bg: alpha('neon', 0.12), border: alpha('neon', 0.33) },
  info: { fg: inks.hudCyan, bg: alpha('hudCyan', 0.12), border: alpha('hudCyan', 0.33) },
  warn: { fg: inks.hudAmber, bg: alpha('hudAmber', 0.12), border: alpha('hudAmber', 0.33) },
  danger: { fg: inks.hudRed, bg: alpha('hudRed', 0.12), border: alpha('hudRed', 0.33) },
  neutral: { fg: 'rgb(var(--c-txt-2))', bg: 'rgb(var(--c-panel))', border: 'var(--c-bd)' },
};

const SIZE_HEIGHT: Record<PillSize, number> = { xs: 18, sm: 22, md: 26 };

/**
 * Pill — rounded label, optionally dismissible. Use for filters, tags, and
 * inline multi-select chips. For static read-only status, prefer `Badge`.
 */
export const Pill = forwardRef<HTMLDivElement, ForgePillProps>(function Pill(
  {
    variant = 'neutral',
    size = 'sm',
    dismissible = false,
    onDismiss,
    children,
    className,
    style,
    dismissLabel = 'Remove',
  },
  ref,
) {
  const palette = VARIANT_TO_COLOR[variant];
  const height = SIZE_HEIGHT[size];

  return (
    <MantinePill
      ref={ref}
      size={size}
      withRemoveButton={false}
      {...(className !== undefined ? { className } : {})}
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        height,
        paddingInline: 10,
        fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontWeight: 500,
        ...style,
      }}
    >
      <Group gap={6} wrap="nowrap" align="center">
        <span>{children}</span>
        {dismissible ? (
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            aria-label={dismissLabel}
            onClick={(e) => {
              e.stopPropagation();
              onDismiss?.();
            }}
            style={{ color: palette.fg }}
          >
            <X size={12} />
          </ActionIcon>
        ) : null}
      </Group>
    </MantinePill>
  );
});
